// Shared, dependency-free HTML sanitizer + detector for admin-authored
// proposal / contract copy that is rendered to clients.
//
// The proposal & contract body fields are stored as HTML (produced by the
// proposal generator). They must be RENDERED as HTML on the client portal —
// otherwise the client sees raw <p>/<strong>/<ul> tags. Because some of that
// copy interpolates stranger-supplied lead data (client name, venue, guest
// count, etc.), we strip the obvious XSS vectors before rendering. For a
// hardened solution later, the internals can be swapped for DOMPurify without
// touching callers.

const STRIP_TAGS =
  'script, style, iframe, object, embed, link, meta, base, form, input, button, textarea, select';

export function sanitizeHtml(dirty) {
  if (!dirty || typeof dirty !== 'string') return '';
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
    return '';
  }
  try {
    const doc = new DOMParser().parseFromString(dirty, 'text/html');
    doc.querySelectorAll(STRIP_TAGS).forEach((el) => el.remove());
    doc.querySelectorAll('*').forEach((el) => {
      Array.from(el.attributes).forEach((attr) => {
        const name = attr.name.toLowerCase();
        const value = (attr.value || '').replace(/\s+/g, '').toLowerCase();
        if (name.startsWith('on')) {
          el.removeAttribute(attr.name);
        } else if (
          (name === 'href' || name === 'src' || name === 'xlink:href') &&
          value.startsWith('javascript:')
        ) {
          el.removeAttribute(attr.name);
        } else if (name === 'style' && /expression|javascript:|url\(/.test(value)) {
          el.removeAttribute(attr.name);
        }
      });
    });
    // Tidy obvious generation artifacts before returning: empty list items /
    // empty lists, and a dangling bullet glyph left when AI-written copy opened
    // a list (or a "…:" lead-in) it never actually filled — otherwise the
    // client sees a stray "•" at the end of a section.
    const DANGLING_BULLET = /[\s\u00A0:;,\u2013\u2014-]*[\u2022\u00B7\u2023\u25AA\u25E6\u2043]+[\s\u00A0]*$/;
    doc.querySelectorAll('p, li, span, h1, h2, h3, h4').forEach((el) => {
      if (el.children.length === 0 && DANGLING_BULLET.test(el.textContent)) {
        el.textContent = el.textContent.replace(DANGLING_BULLET, '');
      }
    });
    doc.querySelectorAll('li').forEach((li) => { if (!li.textContent.trim()) li.remove(); });
    doc.querySelectorAll('ul, ol').forEach((list) => { if (!list.querySelector('li')) list.remove(); });
    doc.querySelectorAll('p').forEach((p) => { if (!p.textContent.trim() && p.children.length === 0) p.remove(); });
    return doc.body.innerHTML;
  } catch (e) {
    return '';
  }
}

// True when the string contains real HTML tags (so it should be rendered as
// HTML). Plain text — including text with only newlines — returns false and is
// left to render as pre-wrapped plain text.
export function isHtmlContent(content) {
  return typeof content === 'string' && /<\/?[a-z][\s\S]*>/i.test(content);
}
